import { AlertCircle, CheckCircle2, Clock3, Printer } from 'lucide-react'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import type { WbsTransaction } from '@/app/services/wbs.service'

interface WbsTransactionResultCardProps {
  transaction: WbsTransaction
  canComplete: boolean
  isCompleting: boolean
  onComplete: () => void
}

interface WbsTransactionLookupErrorCardProps {
  title: string
  message: string
}

const pendingStatuses = new Set<WbsTransaction['status']>([
  'CREATED',
  'SENDING',
  'PENDING',
  'CONSULTING',
])

function money(value: string | number | null | undefined): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(Number(value || 0))
}

function transactionDate(value: string): string {
  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
}

export function WbsTransactionResultCard({
  transaction,
  canComplete,
  isCompleting,
  onComplete,
}: WbsTransactionResultCardProps) {
  const isSuccess = transaction.status === 'SUCCESS'
  const isPending = pendingStatuses.has(transaction.status)
  const commission = transaction.type === 'SERVICE_PAYMENT'
    ? Number(transaction.commission_amount || 0)
    : 0
  const total = Number(transaction.amount || 0) + commission

  const tone = isSuccess
    ? {
        container: 'border-emerald-200 bg-emerald-50/70',
        title: 'text-emerald-800',
        icon: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
        label: 'Exitosa',
        badge: 'border-emerald-200 bg-emerald-100 text-emerald-700',
      }
    : isPending
      ? {
          container: 'border-blue-200 bg-blue-50/70',
          title: 'text-blue-800',
          icon: <Clock3 className="h-5 w-5 text-blue-600" />,
          label: 'Pendiente',
          badge: 'border-blue-200 bg-blue-100 text-blue-700',
        }
      : {
          container: 'border-red-200 bg-red-50/70',
          title: 'text-red-800',
          icon: <AlertCircle className="h-5 w-5 text-red-600" />,
          label: transaction.status === 'EXPIRED' ? 'Expirada' : 'Fallida',
          badge: 'border-red-200 bg-red-100 text-red-700',
        }

  return (
    <section
      aria-label="Resultado de la transacción"
      className={`rounded-lg border p-4 ${tone.container}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {tone.icon}
          <div>
            <h3 className={`text-sm font-semibold ${tone.title}`}>Resultado de la transacción</h3>
            <p className="mt-0.5 text-xs text-slate-600">
              {transaction.wbs_description || 'Sin descripción adicional del proveedor.'}
            </p>
          </div>
        </div>
        <Badge variant="outline" className={tone.badge}>{tone.label}</Badge>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 text-xs sm:grid-cols-2">
        <div>
          <dt className="text-slate-500">Tipo</dt>
          <dd className="font-medium text-slate-800">
            {transaction.type === 'SERVICE_PAYMENT' ? 'Pago de servicio' : 'Recarga'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Operadora</dt>
          <dd className="font-medium text-slate-800">
            {transaction.carrier_description || transaction.carrier_id}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Referencia</dt>
          <dd className="break-all font-medium text-slate-800">{transaction.reference}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Fecha</dt>
          <dd className="font-medium text-slate-800">{transactionDate(transaction.created_at)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Folio POS</dt>
          <dd className="break-all font-mono text-slate-800">{transaction.folio_pos}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Folio Recarga-Me</dt>
          <dd className="break-all font-mono text-slate-800">{transaction.wbs_folio || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Confirmación</dt>
          <dd className="font-mono font-medium text-slate-800">{transaction.wbs_confirmation || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Venta asociada</dt>
          <dd className="font-medium text-slate-800">
            {transaction.sale_id ? `Venta #${transaction.sale_id}` : 'Sin venta registrada'}
          </dd>
        </div>
      </dl>

      <div className="mt-4 rounded-md border border-white/80 bg-white/80 p-3 text-xs">
        <div className="flex justify-between text-slate-600">
          <span>Monto</span>
          <span>{money(transaction.amount)}</span>
        </div>
        <div className="mt-1 flex justify-between text-slate-600">
          <span>Comisión</span>
          <span>{money(commission)}</span>
        </div>
        <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-sm font-semibold text-slate-900">
          <span>Total</span>
          <span>{money(total)}</span>
        </div>
      </div>

      {isSuccess && !transaction.sale_id && (
        <div className="mt-4">
          <Button
            type="button"
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            disabled={!canComplete || isCompleting}
            onClick={onComplete}
          >
            <Printer className="mr-2 h-4 w-4" />
            {isCompleting ? 'Registrando venta...' : 'Completar venta e imprimir ticket'}
          </Button>
          {!canComplete && (
            <p className="mt-2 text-center text-xs text-red-600">
              Se requiere una caja abierta para completar la venta.
            </p>
          )}
        </div>
      )}
    </section>
  )
}

export function WbsTransactionLookupErrorCard({
  title,
  message,
}: WbsTransactionLookupErrorCardProps) {
  return (
    <section
      aria-label="Resultado de la transacción"
      className="rounded-lg border border-red-500/40 bg-red-500/10 p-4"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />

        <div>
          <h3 className="text-sm font-semibold text-red-500">
            {title}
          </h3>

          <p className="mt-1 text-xs text-red-400">
            {message}
          </p>
        </div>
      </div>
    </section>
  )
}
